// Topbar global search. Sits in AppShell + AdminShell headers.
//
// Behaviour:
//   - Cmd-K (or Ctrl-K) focuses the input from anywhere in the app.
//   - Typing debounces 200ms before hitting /api/v1/search.
//   - Results render in a popover dropdown grouped by `kind`
//     (Packages and artifacts vs. Repositories) with up to 20 hits.
//   - Arrow up / down moves the highlight; Enter navigates to the
//     selected hit; Esc closes the popover.
//   - Clicking outside closes the popover.
//
// Navigation targets:
//   - kind="repo"      → /repos/:id     (RepoDetail)
//   - kind="artifact"  → /artifacts/:id (ArtifactDetail)
//
// The dropdown shows the kind, format badge, title, and a subtitle
// with project/repo/path context. Empty state vs. no-results vs.
// loading are all distinguishable.

import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Box, Package, X, Command } from 'lucide-react';
import { useSearch, type SearchHit } from '../api/search';
import { CopyIconButton } from './ui/CopyIconButton';
import { cn } from '../lib/cn';

function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}

function formatTone(format?: string): string {
  switch (format) {
    case 'maven':
    case 'gradle':
      return 'text-orange-400';
    case 'npm':
      return 'text-red-400';
    case 'pypi':
      return 'text-blue-400';
    case 'docker':
      return 'text-sky-400';
    case 'helm':
      return 'text-cyan-400';
    case 'go':
      return 'text-teal-400';
    case 'nuget':
      return 'text-violet-400';
    default:
      return 'text-fg-subtle';
  }
}

export function SearchBar() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Debounce the input.
  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebounced(query), 200);
    return () => globalThis.clearTimeout(t);
  }, [query]);

  const { hits, isLoading } = useSearch(debounced, 20);

  // Group artifacts and repos so the dropdown can render two
  // sections. We keep the API-returned ordering inside each group.
  const grouped = useMemo(() => {
    const artifacts: SearchHit[] = [];
    const repos: SearchHit[] = [];
    for (const h of hits) {
      if (h.kind === 'repo') repos.push(h);
      else artifacts.push(h);
    }
    return { artifacts, repos, all: [...artifacts, ...repos] };
  }, [hits]);

  // Reset highlight when the result set changes.
  useEffect(() => {
    setHighlight(0);
  }, [debounced, hits.length]);

  // Cmd-K / Ctrl-K to focus the field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, []);

  // Click outside to close.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const onPick = (hit: SearchHit) => {
    setOpen(false);
    setQuery('');
    setDebounced('');
    if (hit.kind === 'repo') {
      navigate(`/repos/${hit.id}`);
    } else {
      navigate(`/artifacts/${hit.id}`);
    }
  };

  const goToSearchPage = () => {
    const term = (debounced || query).trim();
    if (!term) return;
    setOpen(false);
    setQuery('');
    setDebounced('');
    navigate(`/search?q=${encodeURIComponent(term)}`);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // The "View all results" footer is one slot past the last hit;
      // moving down from the last hit lands the highlight there.
      setHighlight((h) => Math.min(h + 1, grouped.all.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      // Cmd/Ctrl-Enter always jumps to the full /search page even if
      // the user has a hit highlighted — handy for "show me everything"
      // when the popover only shows a slice.
      const meta = e.metaKey || e.ctrlKey;
      if (meta) {
        e.preventDefault();
        goToSearchPage();
        return;
      }
      // Highlight === grouped.all.length is the synthetic
      // "View all results" footer slot.
      if (highlight === grouped.all.length) {
        e.preventDefault();
        goToSearchPage();
        return;
      }
      const hit = grouped.all[highlight];
      if (hit) {
        e.preventDefault();
        onPick(hit);
        return;
      }
      // No hit highlighted (popover empty / loading) — fall back to
      // the dedicated search page so the user always has somewhere
      // to go on Enter.
      e.preventDefault();
      goToSearchPage();
    }
  };

  const showPopover =
    open && (debounced.length > 0 || isLoading);

  return (
    <div ref={containerRef} className="relative w-full max-w-3xl">
      <div
        className={cn(
          'flex items-center h-9 px-3 rounded-md border transition-colors',
          'bg-bg-inset text-fg',
          open
            ? 'border-accent bg-bg-elevated'
            : 'border-border hover:border-border-strong',
        )}
      >
        <Search className="h-4 w-4 text-fg-subtle shrink-0" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="Search packages and artifacts…"
          className={cn(
            'flex-1 bg-transparent outline-none text-sm px-2.5',
            'placeholder:text-fg-subtle',
          )}
          spellCheck={false}
          autoComplete="off"
        />
        {query ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQuery('');
              setDebounced('');
              setOpen(false);
              inputRef.current?.focus();
            }}
            className="h-6 w-6 inline-flex items-center justify-center rounded text-fg-subtle hover:text-fg hover:bg-bg-hover"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span
            className="hidden md:inline-flex items-center gap-1 px-1.5 h-5 rounded border border-border text-[10px] text-fg-subtle font-mono"
            title="Press Cmd+K to focus search"
          >
            {isMac() ? <Command className="h-3 w-3" /> : <span>Ctrl</span>}
            <span>K</span>
          </span>
        )}
      </div>

      {showPopover && (
        <div
          className={cn(
            'absolute left-0 right-0 top-full mt-2 z-50',
            'rounded-md border border-border bg-bg-elevated shadow-xl',
            'max-h-[28rem] overflow-y-auto',
          )}
        >
          <SearchPopoverContent
            isLoading={isLoading}
            grouped={grouped}
            debounced={debounced}
            highlight={highlight}
            onHover={setHighlight}
            onPick={onPick}
            goToSearchPage={goToSearchPage}
          />
        </div>
      )}
    </div>
  );
}

interface GroupedHits {
  artifacts: SearchHit[];
  repos: SearchHit[];
  all: SearchHit[];
}

interface SearchPopoverContentProps {
  readonly isLoading: boolean;
  readonly grouped: GroupedHits;
  readonly debounced: string;
  readonly highlight: number;
  readonly onHover: (i: number) => void;
  readonly onPick: (h: SearchHit) => void;
  readonly goToSearchPage: () => void;
}

function SearchPopoverContent({
  isLoading,
  grouped,
  debounced,
  highlight,
  onHover,
  onPick,
  goToSearchPage,
}: SearchPopoverContentProps) {
  if (isLoading && grouped.all.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-fg-muted">
        Searching…
      </div>
    );
  }
  if (grouped.all.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-fg-muted">
        No matches for <span className="orb-code">{debounced}</span>.
      </div>
    );
  }
  return (
    <>
      {grouped.artifacts.length > 0 && (
        <Section
          title="Packages & artifacts"
          items={grouped.artifacts}
          startIndex={0}
          highlight={highlight}
          onHover={onHover}
          onPick={onPick}
        />
      )}
      {grouped.repos.length > 0 && (
        <Section
          title="Repositories"
          items={grouped.repos}
          startIndex={grouped.artifacts.length}
          highlight={highlight}
          onHover={onHover}
          onPick={onPick}
        />
      )}
      {/* v186 — full results footer slot. The dedicated
          /search page can apply filters and paginate; the
          popover just shows the top ~20. */}
      <button
        type="button"
        onMouseEnter={() => onHover(grouped.all.length)}
        onClick={goToSearchPage}
        className={cn(
          'w-full text-left px-4 py-2 text-xs border-t border-border',
          highlight === grouped.all.length
            ? 'bg-accent/10 text-accent'
            : 'text-fg-muted hover:bg-bg-hover/60',
        )}
      >
        View all results for{' '}
        <span className="orb-code">{debounced}</span> →
      </button>
    </>
  );
}

interface SectionProps {
  readonly title: string;
  readonly items: SearchHit[];
  readonly startIndex: number;
  readonly highlight: number;
  readonly onHover: (i: number) => void;
  readonly onPick: (h: SearchHit) => void;
}

function Section({ title, items, startIndex, highlight, onHover, onPick }: SectionProps) {
  const onRowKeyDown = (e: ReactKeyboardEvent<HTMLLIElement>, hit: SearchHit) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onPick(hit);
    }
  };

  return (
    <div className="py-2">
      <p className="orb-eyebrow text-fg-subtle px-4 pb-2 pt-1">{title}</p>
      <ul className="space-y-0.5">
        {items.map((hit, i) => {
          const idx = startIndex + i;
          const active = idx === highlight;
          return (
            <li
              key={`${hit.kind}-${hit.id}`}
              role="link"
              tabIndex={0}
              onMouseEnter={() => onHover(idx)}
              onClick={() => onPick(hit)}
              onKeyDown={(e) => onRowKeyDown(e, hit)}
              className={cn(
                'flex items-start gap-3 px-4 py-2 text-sm cursor-pointer focus:outline-none',
                active ? 'bg-accent/10' : 'hover:bg-bg-hover/60',
              )}
            >
              <span className="mt-0.5 shrink-0">
                {hit.kind === 'repo' ? (
                  <Box className={cn('h-4 w-4', formatTone(hit.format))} />
                ) : (
                  <Package className={cn('h-4 w-4', formatTone(hit.format))} />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-fg font-medium truncate" title={hit.title}>
                    {hit.title}
                  </span>
                  {hit.format && (
                    <span className="orb-code text-[10px] shrink-0">{hit.format}</span>
                  )}
                  {hit.version && (
                    <span className="text-fg-subtle text-xs shrink-0">{hit.version}</span>
                  )}
                </span>
                <span className="flex items-center gap-1">
                  <span className="block min-w-0 flex-1 text-xs text-fg-muted truncate" title={hit.subtitle}>
                    {hit.subtitle}
                  </span>
                  <CopyIconButton value={hit.subtitle} label="Copy full value" stopPropagation />
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
